import React, { useMemo, useState } from 'react';
import { Tabs } from 'antd';
import type { Route } from '../../../types';
import type { RouteViewModel } from '../../../types/routeViewModel';
import type { WorkspaceTask } from '../../../store/api/workspaceApi';
import { useGetRouteTaskHistoryQuery } from '../../../store/api/workspaceApi';
import { buildClientShortNameLookupFromRouteApplications } from '../../../domain/scopedTaskPresentation';
import { WorkspaceRouteTasks } from '../../workspace/WorkspaceTasks';
import ApplicationTaskModalHost from '../../applications/ApplicationTaskModalHost';
import TaskHistoryTable from '../../tasks/TaskHistoryTable';

function carrierAgreementFromView(view: RouteViewModel): WorkspaceTask['carrierAgreement'] {
  if (view.operation.isCompanyRoute) return null;
  const number = view.operation.carrierContractRequest.number?.trim() || null;
  const date = view.operation.carrierContractRequest.date || null;
  if (!number && !date) return null;
  return { number, date };
}

export interface RouteTasksTabProps {
  route: Route;
  view: RouteViewModel;
  onSuccess?: () => void;
}

const RouteTasksTab: React.FC<RouteTasksTabProps> = ({ route, view, onSuccess }) => {
  const [activeTask, setActiveTask] = useState<WorkspaceTask | null>(null);
  const [activeTab, setActiveTab] = useState<'active' | 'history'>('active');

  const tasks = useMemo<WorkspaceTask[]>(
    () =>
      view.execution.tasks.map((task) => ({
        id: task.id,
        taskType: task.taskType,
        status: task.status,
        dueDate: task.dueDate,
        routeId: route.id,
        applicationId: null,
        entityType: 'ROUTE',
        invoiceId: null,
        taskScope: task.taskScope ?? null,
        groupId: task.groupId ?? null,
        groupLabel: task.groupLabel ?? null,
        routeStopId: task.routeStopId ?? null,
        stopLabel: task.stopLabel ?? null,
        stopType: task.stopType ?? null,
        transportType: task.transportType ?? null,
        routeStatus: view.operation.status,
        loadingCity: view.operation.direction.loadingCity,
        unloadingCity: view.operation.direction.unloadingCity,
        carrierName:
          view.operation.isCompanyRoute
            ? 'Свой'
            : typeof view.parties.carrier?.name === 'string' && view.parties.carrier.name.trim()
              ? view.parties.carrier.name.trim()
              : null,
        carrierAgreement: carrierAgreementFromView(view),
      })),
    [route.id, view]
  );

  const presentationContext = useMemo(
    () => ({
      clientShortNameByGroupId: buildClientShortNameLookupFromRouteApplications(
        view.applications,
        view.structure?.applicationGroups
      ),
    }),
    [view.applications, view.structure?.applicationGroups]
  );

  const {
    data: historyPage,
    isLoading: historyLoading,
    isFetching: historyFetching,
  } = useGetRouteTaskHistoryQuery(route.id, {
    skip: activeTab !== 'history',
  });

  const historyItems = historyPage?.items ?? [];

  return (
    <>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key === 'history' ? 'history' : 'active')}
        items={[
          {
            key: 'active',
            label: 'Активные',
            children: (
              <WorkspaceRouteTasks
                tasks={tasks}
                presentationContext={presentationContext}
                onTaskAction={setActiveTask}
                activeActionTaskId={null}
                maxHeight={520}
                bodyPadding={8}
                groupTasksPadding="4px 8px 6px"
                title="Задачи маршрута"
              />
            ),
          },
          {
            key: 'history',
            label: 'История',
            children: (
              <TaskHistoryTable
                items={historyItems}
                loading={historyLoading || historyFetching}
              />
            ),
          },
        ]}
      />
      <ApplicationTaskModalHost
        task={activeTask}
        onClose={() => setActiveTask(null)}
        onSuccess={() => {
          onSuccess?.();
          setActiveTask(null);
        }}
      />
    </>
  );
};

export default RouteTasksTab;
